import { createSelector } from 'reselect';
import { FeaturesApi } from './FeaturesApi';
import { isPresent } from 'utils/isPresent';

const FEATURES_NAMESPACE = 'features';
export const FEATURE_ADD_JOBS = 'add_jobs';
export const FEATURE_SALES_PROPOSAL = 'sales_proposal';
export const FEATURE_CHECKLISTS = 'checklists';
export const FEATURE_JOB_COST = 'job_cost';
export const FEATURE_PROPERTY_PROFILE = 'property_profile';
export const FEATURE_SERVICE_AGREEMENTS = 'service_agreements';
export const FEATURE_PREMIUM_REVIEWS = 'premium_reviews';
export const FEATURE_CALL_RAIL = 'call_rail';
export const FEATURE_QUICKBOOKS_DESKTOP = 'quickbooks_desktop';
export const FEATURE_QUICKBOOKS_ONLINE = 'quickbooks';
export const FEATURE_EMPLOYEE_CUSTOM_REPORT = 'employee_custom_report';
export const FEATURE_PURCHASE_ORDERS = 'purchase_orders';
export const FEATURE_CUSTOM_FIELDS = 'custom_fields';
export const FEATURE_E_COMMERCE = 'e_commerce';
export const FEATURE_VISUAL_PRICE_BOOK = 'visual_price_book';
export const FEATURE_FLAT_RATE_PRICING = 'flat_rate_pricing';
export const FEATURE_PROFIT_RHINO = 'profit_rhino';
export const FEATURE_TIME_TRACKING_BY_TECH = 'time_tracking_by_tech';
export const FEATURE_EMPLOYEE_TIME_TRACKING = 'employee_time_tracking';
export const FEATURE_LEAD_SOURCE = 'lead_source';
export const TECH_LEADERBOARD = 'tech_leaderboard';
const FEATURE_CALL_TRACKING = 'call_tracking';
const FEATURE_VOIP = 'voip';
export const FEATURE_SALES_MANAGEMENT = 'sales_management';
export const FEATURE_ENHANCED_JOB_COSTING = 'job_cost';
export const FEATURE_INVOICES_LITE = 'invoices_lite';
export const FEATURE_CUSTOMERS = 'customers';
export const FEATURE_ONLINE_PAYMENTS = 'online_payments';

export const getOrganizationFeatures = createSelector(
  (state) => state[FEATURES_NAMESPACE],
  (features) => {
    return isPresent(features) && isPresent(features.data) ? features.data : [];
  }
);

export const getMultiTechFeatures = createSelector(
  (state) => state[FEATURES_NAMESPACE],
  (features) => {
    return features?.can_see_multitech_view;
  }
);

export const hasOrganizationFeaturesLoaded = createSelector(getOrganizationFeatures, (features) => isPresent(features));

export const getFeature = (featureName) =>
  createSelector(getOrganizationFeatures, (features) => features.find((feature) => feature.name === featureName));

export const canUseFeature = (featureName) =>
  createSelector(getFeature(featureName), (feature) => isPresent(feature) && feature.enabled);

export const canUseTimeTrackingByTech = canUseFeature(FEATURE_TIME_TRACKING_BY_TECH);

export const hasEmployeeTimeTracking = getFeature(FEATURE_EMPLOYEE_TIME_TRACKING);

export const hasTimeTrackingByTech = getFeature(FEATURE_TIME_TRACKING_BY_TECH);

export const hasTimeTrackingFeatures = hasEmployeeTimeTracking || hasTimeTrackingByTech;

export const canUseSalesProposal = canUseFeature(FEATURE_SALES_PROPOSAL);

export const canUseAddJobs = canUseFeature(FEATURE_ADD_JOBS);

export const canUseChecklists = canUseFeature(FEATURE_CHECKLISTS);

export const canUsePropertyProfile = canUseFeature(FEATURE_PROPERTY_PROFILE);

export const canUsePremiumReviews = canUseFeature(FEATURE_PREMIUM_REVIEWS);

export const canUseCallRail = canUseFeature(FEATURE_CALL_RAIL);

export const canUseQuickbooksDesktop = canUseFeature(FEATURE_QUICKBOOKS_DESKTOP);

export const canUseQuickbooksOnline = canUseFeature(FEATURE_QUICKBOOKS_ONLINE);

export const getQboFeature = getFeature(FEATURE_QUICKBOOKS_ONLINE);

export const canUseJobCost = canUseFeature(FEATURE_JOB_COST);

export const canUseServiceAgreements = canUseFeature(FEATURE_SERVICE_AGREEMENTS);

export const canUseEmployeeCustomReports = canUseFeature(FEATURE_EMPLOYEE_CUSTOM_REPORT);

export const canUseVisualPriceBook = canUseFeature(FEATURE_VISUAL_PRICE_BOOK);

export const canUseFlatRatePricing = canUseFeature(FEATURE_FLAT_RATE_PRICING);

export const canUseProfitRhino = canUseFeature(FEATURE_PROFIT_RHINO);

export const canUseLeadSource = canUseFeature(FEATURE_LEAD_SOURCE);

export const canUseTechLeaderboardBillingFeature = canUseFeature(TECH_LEADERBOARD);

export const canUseCallTracking = canUseFeature(FEATURE_CALL_TRACKING);

export const canUseVoip = canUseFeature(FEATURE_VOIP);

export const canUseSalesManagement = canUseFeature(FEATURE_SALES_MANAGEMENT);

export const hasEnhancedJobCosting = getFeature(FEATURE_ENHANCED_JOB_COSTING);

export const canUseEnhancedJobCosting = canUseFeature(FEATURE_JOB_COST);

export const isCallTrackingAddOnFeature = ({ featureName }) => featureName === FEATURE_CALL_TRACKING;

/**
 * @deprecated  To fetch an org's enabled features, use the `useBillingEnabledFeatures` hook instead.
 */
export const fetchOrganizationFeatures = {
  fetchingApiFunc: FeaturesApi.index,
  selector: getOrganizationFeatures,
  dataNamespace: FEATURES_NAMESPACE,
  uniqueCacheKey: '4bc0254d-6a68-4449-b91d-764b3c8219c3',
};

/** 
 * @deprecated To determine if an organization is on invoices lite, use the following instead:
 * 
 * `useEnabledFeature(BillingPlanFeature.FEATURE_INVOICES_LITE)`
 */
export const canUseInvoicesLite = canUseFeature(FEATURE_INVOICES_LITE);

export const canUseCustomers = canUseFeature(FEATURE_CUSTOMERS);

export const canUseOnlinePayments = canUseFeature(FEATURE_ONLINE_PAYMENTS);
